import { useEffect, useState } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'

import * as fetchs from '../fetchs'
import * as state from '../states'
import {
	EFetchReloadKey,
	ITokensState,
	ICommonCountry,
	IOfferAffiliateNetwork,
	IFetchListSpendBuyer,
	IAccountBuyer,
	IFetchRevenueAgencyDropDown,
	IRevenueAgencyDropDown,
	IFetchAccountTeam
} from '../types'
import { INoFetch } from '../types'
import { useCommonFetch } from './hook.useCommonFetch'
import { useCommonFetchAutoRefresh } from './hook.useCommonFetchAutoRefresh'
import * as helpers from '../helpers'

type IErrorKey =
	| 'errorAccountBuyers'
	| 'errorAccountTeams'
	| 'errorRevenueAgencies'
	| 'errorOfferGroups'
	| 'errorCountry'
	| 'errorAffiliateNetwork'

type IUseCommonFetchsIn = {
	silentMode?: boolean
}

type IUseCommonFetchsOut = {
	errors: Record<IErrorKey, INoFetch | null>
}
export const useCommonFetchs = ({
	silentMode = false
}: IUseCommonFetchsIn): IUseCommonFetchsOut => {
	const tokens: ITokensState = useAtomValue(state.tokensAtom)
	const access = tokens?.access ?? null

	const setFetchIsLoading = useSetAtom(state.fetchIsLoadingAtom)
	const fetchIsCommonToReload = useAtomValue(state.fetchIsCommonToReloadAtom)

	const setOfferGroups = useSetAtom(state.offerGroupsAtom)
	const setAccountBuyers = useSetAtom(state.accountBuyersAtom)
	const setAccountTeams = useSetAtom(state.accountTeamsAtom)
	const setRevenueAgencies = useSetAtom(state.revenueAgenciesAtom)
	const setCountryOptions = useSetAtom(state.countryOptionsAtom)
	const setAffiliateNetworkOptions = useSetAtom(state.affiliateNetworkOptionsAtom)

	const [errorOfferGroups, setErrorOfferGroups] = useState<INoFetch | null>(null)
	const [errorCountry, setErrorCountry] = useState<INoFetch | null>(null)
	const [errorAffiliateNetwork, setErrorAffiliateNetwork] = useState<INoFetch | null>(null)

	// atom fetcher for - listAccountBuyer
	const { error: errorAccountBuyers } = useCommonFetch<IFetchListSpendBuyer, IAccountBuyer>({
		fetcher: fetchs.listAccountBuyer,
		jotaiSetter: setAccountBuyers,
		reloadKeyName: EFetchReloadKey.listAccountBuyer,
		formatter: (items) => {
			//console.log('useCommonFetch: listAccountBuyer: formatter: ', { items })
			return items as IAccountBuyer[]
		},
		silentMode
	})

	/* ['auto-refresh-update' logic for 'listAccountBuyer' - atom fetcher] */
	useCommonFetchAutoRefresh({
		isEnabled: true,
		reloadKeyName: EFetchReloadKey.listAccountBuyer
	})

	// atom fetcher for - listAccountTeam
	const { error: errorAccountTeams } = useCommonFetch<IFetchAccountTeam, IFetchAccountTeam>({
		fetcher: fetchs.listAccountTeam,
		jotaiSetter: setAccountTeams,
		reloadKeyName: EFetchReloadKey.listAccountTeam,
		formatter: (items) => {
			//console.log('useCommonFetch: listAccountTeam: formatter: ', { items })
			return items as IFetchAccountTeam[]
		},
		silentMode
	})

	/* ['auto-refresh-update' logic for 'listAccountTeam' - atom fetcher] */
	useCommonFetchAutoRefresh({
		isEnabled: true,
		reloadKeyName: EFetchReloadKey.listAccountTeam
	})

	// atom fetcher for - listRevenueAgency
	const { error: errorRevenueAgencies } = useCommonFetch<IFetchRevenueAgencyDropDown, IRevenueAgencyDropDown>({
		fetcher: fetchs.listRevenueAgencyDropDown,
		jotaiSetter: setRevenueAgencies,
		reloadKeyName: EFetchReloadKey.listRevenueAgency,
		formatter: (items) => {
			//console.log('useCommonFetch: listRevenueAgency: formatter: ', { items })
			return items as IRevenueAgencyDropDown[]
		},
		silentMode
	})

	/* ['auto-refresh-update' logic for 'listRevenueAgency' - atom fetcher] */
	useCommonFetchAutoRefresh({
		isEnabled: true,
		reloadKeyName: EFetchReloadKey.listRevenueAgency
	})

	/* ['auto-refresh-update' logic for 'allParallelList' - common fetchers in parallel] */
	useCommonFetchAutoRefresh({
		isEnabled: true,
		reloadKeyName: EFetchReloadKey.allParallelList
	})

	const reloadKeyDepAllParallelList = fetchIsCommonToReload[EFetchReloadKey.allParallelList].count
	const reloadKeyDepAllList = fetchIsCommonToReload[EFetchReloadKey.allList].count

	// common fetchers (in parallel) for - listOfferGroup/listCommonCountry/listOfferAffiliateNetwork
	useEffect(() => {
		const controller = new AbortController()
		const signal = controller.signal

		const call = async () => {
			try {
				!silentMode && setFetchIsLoading(state.turnOnLoader)
				// to launch common fetchers in parallel here
				const [offerGroup, commonCountry, offerAffiliateNetwork] = await Promise.allSettled([
					fetchs.listOfferGroup(signal, access),
					fetchs.listCommonCountry(signal, access),
					fetchs.listOfferAffiliateNetwork(signal, access)
				])
				// listOfferGroup
				if (offerGroup?.status === 'fulfilled' && offerGroup?.value?.items) {
					setErrorOfferGroups(null)
					setOfferGroups(offerGroup.value.items)
				} else if (offerGroup?.status === 'rejected') {
					setErrorOfferGroups(offerGroup.reason as INoFetch)
					setOfferGroups(null)
				}
				// listCommonCountry
				if (commonCountry?.status === 'fulfilled' && commonCountry?.value?.items) {
					const commonCountryMap: Array<ICommonCountry> = commonCountry.value.items.map(item => ({
						...item,
						value: item.label
					}))
					setErrorCountry(null)
					setCountryOptions(commonCountryMap)
				} else if (commonCountry?.status === 'rejected') {
					setErrorCountry(commonCountry.reason as INoFetch)
					setCountryOptions(null)
				}
				// listOfferAffiliateNetwork
				if (offerAffiliateNetwork?.status === 'fulfilled' && offerAffiliateNetwork?.value?.items) {
					const offerAffiliateNetworkMap: Array<IOfferAffiliateNetwork> =
						offerAffiliateNetwork.value.items.sort(helpers.strComparator).map(item => ({
							label: item,
							value: item
						}))
					setErrorAffiliateNetwork(null)
					setAffiliateNetworkOptions(offerAffiliateNetworkMap)
				} else if (offerAffiliateNetwork?.status === 'rejected') {
					setErrorAffiliateNetwork(offerAffiliateNetwork.reason as INoFetch)
					setAffiliateNetworkOptions(null)
				}
			} finally {
				!silentMode && setFetchIsLoading(state.turnOffLoader)
			}
		}
		call()
		return () => {
			controller.abort()
		}
	}, [
		reloadKeyDepAllParallelList,
		reloadKeyDepAllList,
		access,
		setAffiliateNetworkOptions,
		setCountryOptions,
		setOfferGroups,
		setFetchIsLoading
	])

	return {
		errors: {
			errorAccountBuyers,
			errorAccountTeams,
			errorRevenueAgencies,
			errorOfferGroups,
			errorAffiliateNetwork,
			errorCountry
		}
	}
}